import React, { useCallback } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useUserSettings } from 'src/Hooks'
import { useOpenInexogyModal } from '../../../hooks/inexogy'
import { SmartMeterRecommendationModalProps } from '../../../types'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { useTypedSelector } from 'src/State/RootReducer'

/**
 * Recommends ordering a smart meter (via Inexogy) before signing up for an ai360 tariff which is not yet connected.
 */
const SmartMeterRecommendationModal: React.FC<SmartMeterRecommendationModalProps> = ({ onClose, onContinue, tariffType, place = 0 }) => {
  const { t } = useCustomTranslation()
  const openInexogyModal = useOpenInexogyModal()
  const { userSettings, updateUserSettings } = useUserSettings()
  const { pageActions } = useNewRelic()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const activeInstallationId = useTypedSelector(state => state.storage.activeInstallation)

  /* Start showing the ContinueAi360TariffOrderBanner reminder, for this tariff and installation, until the order goes through */
  const handleOrderSmartMeter = useCallback(() => {
    updateUserSettings({
      ai360SmartMeterOrder: {
        tariffType,
        installationId: activeInstallationId,
      },
    })
    pageActions.componentClicked({
      componentName: 'SmartMeterRecommendationModal',
      elementName: `${tariffType}OrderSmartMeterButton`,
      userId: userSettings.userid?.toString(),
    })

    /* On mobile the Inexogy modal opens full-screen, so this dialog needs to get out of the way */
    if (isMobile) onClose()

    openInexogyModal()
  }, [activeInstallationId, isMobile, onClose, openInexogyModal, pageActions, tariffType, updateUserSettings, userSettings.userid])

  /* tariffType tells apart which details dialog (ai360PowerFix / ai360PowerShare) this modal was opened from */
  const handleContinue = useCallback(() => {
    pageActions.componentClicked({
      componentName: 'SmartMeterRecommendationModal',
      elementName: `${tariffType}ContinueButton`,
      userId: userSettings.userid?.toString(),
    })

    onContinue()
  }, [onContinue, pageActions, tariffType, userSettings.userid])

  return (
    <Dialog
      maxWidth="sm"
      title={t('installation-extensions.ai360.smart-meter-recommendation.title')}
      onClose={onClose}
      place={place}
      actions={{
        secondary: [
          { name: t('common.cancel') },
          { name: t('installation-extensions.ai360.smart-meter-recommendation.actions.order-smart-meter'), onClick: handleOrderSmartMeter },
        ],
        primary: { name: t('installation-extensions.ai360.smart-meter-recommendation.actions.continue'), onClick: handleContinue },
      }}
    >
      <Typography variant="body2">{t('installation-extensions.ai360.smart-meter-recommendation.description')}</Typography>
    </Dialog>
  )
}

export default SmartMeterRecommendationModal
